import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Settings, Calendar, Receipt, Clock, MessageSquare, MapPin, Save } from 'lucide-react';
import { toast } from 'sonner';

const TenantSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState({
    vacation_management: true,
    payroll_documents: true,
    time_tracking: true,
    chat: true,
    gps_tracking: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get('/admin/settings');
      if (res.data.enabled_features) {
        setFeatures(res.data.enabled_features);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (feature) => {
    setFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put('/admin/settings/features', {
        enabled_features: features
      });
      toast.success('Einstellungen erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const featureList = [
    {
      key: 'vacation_management',
      icon: Calendar,
      title: 'Urlaubsverwaltung',
      description: 'Mitarbeiter können Urlaubsanträge stellen und ihren Urlaubssaldo sehen'
    },
    {
      key: 'payroll_documents',
      icon: Receipt,
      title: 'Lohnabrechnungen',
      description: 'Mitarbeiter können ihre monatlichen Lohnabrechnungen einsehen und herunterladen'
    },
    {
      key: 'time_tracking',
      icon: Clock,
      title: 'Arbeitszeiterfassung',
      description: 'Mitarbeiter können Arbeitszeiten erfassen und Überstunden tracken'
    },
    {
      key: 'chat',
      icon: MessageSquare,
      title: 'Gruppen-Chat',
      description: 'Team-Kommunikation über integrierten Chat'
    },
    {
      key: 'gps_tracking',
      icon: MapPin,
      title: 'GPS Live-Tracking',
      description: 'Echtzeit-Standortverfolgung für Fahrzeuge und Fahrer'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-4xl font-bold text-white flex items-center gap-3">
          <Settings className="h-10 w-10" />
          Unternehmens-Einstellungen
        </h1>
        <p className="text-zinc-400 mt-2">
          Verwalten Sie, welche Features Ihre Mitarbeiter nutzen können
        </p>
      </div>

      <Card className="bg-blue-900/20 border-blue-700">
        <CardContent className="p-4">
          <p className="text-blue-400 text-sm">
            💡 <strong>Hinweis:</strong> Deaktivierte Features werden in der Navigation der Mitarbeiter nicht angezeigt. 
            Sie als Admin haben weiterhin Zugriff auf alle Verwaltungsfunktionen.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Modul-Verwaltung</CardTitle>
          <CardDescription>Aktivieren oder deaktivieren Sie Features für Ihre Mitarbeiter</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {featureList.map((feature) => {
            const Icon = feature.icon;
            const isEnabled = features[feature.key];
            
            return (
              <div
                key={feature.key}
                className={`
                  p-4 rounded-lg border-2 transition-all
                  ${isEnabled 
                    ? 'bg-green-900/20 border-green-700' 
                    : 'bg-zinc-800/50 border-zinc-700'}
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`
                      h-12 w-12 rounded-lg flex items-center justify-center
                      ${isEnabled ? 'bg-green-900/40' : 'bg-zinc-700/40'}
                    `}>
                      <Icon className={`h-6 w-6 ${isEnabled ? 'text-green-400' : 'text-zinc-500'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
                        {isEnabled ? (
                          <span className="text-xs px-2 py-1 rounded bg-green-900/40 text-green-400 border border-green-700">
                            AKTIV
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-zinc-700/40 text-zinc-400 border border-zinc-600">
                            DEAKTIVIERT
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400">{feature.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleToggle(feature.key)}
                      className="data-[state=checked]:bg-green-600"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={loadSettings}
          disabled={saving}
        >
          Zurücksetzen
        </Button>
        <Button
          onClick={handleSave}
          size="lg"
          disabled={saving}
          className="min-w-[200px]"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
          ) : (
            <>
              <Save className="mr-2 h-5 w-5" />
              Änderungen speichern
            </>
          )}
        </Button>
      </div>

      {/* Feature Count Summary */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-6">
          <div className="text-center">
            <p className="text-sm text-zinc-400 mb-2">Aktive Features</p>
            <p className="text-4xl font-bold text-white">
              {Object.values(features).filter(Boolean).length} / {featureList.length}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantSettings;
